import {
  Badge,
  Box,
  Button,
  Flex,
  SimpleGrid,
  Spinner,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { useTranslation } from 'react-i18next';
import useUserDetail from './hooks/useUserDetail';
import BusinessAssociationsPanel from './components/BusinessAssociationsPanel';

export default function UserDetail() {
  const { t } = useTranslation();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const {
    user,
    userBusiness,
    loading,
    saving,
    toggleDelete,
    setOnboardingComplete,
    navigate,
  } = useUserDetail();

  if (loading) {
    return (
      <Flex
        direction="column"
        pt={{ sm: '125px', lg: '75px' }}
        align="center"
        justify="center"
        minH="50vh"
      >
        <Spinner size="xl" color="brand.500" />
      </Flex>
    );
  }

  if (!user) {
    return (
      <Flex direction="column" pt={{ sm: '125px', lg: '75px' }} align="center">
        <Text color="red.500" fontSize="lg">
          User not found
        </Text>
      </Flex>
    );
  }

  const formatDate = (date?: Date | string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  return (
    <Flex direction="column" pt={{ sm: '125px', lg: '75px' }} gap="20px">
      {/* User Info Card */}
      <Card p="30px">
        <Flex justify="space-between" align="start" mb="20px">
          <Box>
            <Text color={textColor} fontSize="2xl" fontWeight="700">
              {user.firstName || user.name || ''}{' '}
              {user.familyName || user.lastName || ''}
            </Text>
            <Text color="gray.500" fontSize="sm">
              {user.email}
            </Text>
          </Box>
          <Flex gap="8px">
            {user.isDeleted && (
              <Badge colorScheme="red" fontSize="md" px="12px" py="4px" borderRadius="8px">
                Deleted
              </Badge>
            )}
            {user.roles?.map((role) => (
              <Badge
                key={role}
                colorScheme="brand"
                fontSize="sm"
                px="10px"
                py="3px"
                borderRadius="8px"
              >
                {t(`user_roles.${role}`) || role}
              </Badge>
            ))}
          </Flex>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap="20px">
          <Box>
            <Text fontSize="sm" color="gray.500">
              Phone
            </Text>
            <Text fontSize="md" color={textColor}>
              {user.phone || '-'}
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.500">
              Display Name
            </Text>
            <Text fontSize="md" color={textColor}>
              {user.displayName || '-'}
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.500">
              Job Title
            </Text>
            <Text fontSize="md" color={textColor}>
              {user.jobTitle || '-'}
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.500">
              Email Verified
            </Text>
            <Badge
              colorScheme={user.isEmailVerified ? 'green' : 'orange'}
              fontSize="sm"
              borderRadius="6px"
            >
              {user.isEmailVerified ? 'Yes' : 'No'}
            </Badge>
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.500">
              Onboarding Step
            </Text>
            <Text fontSize="md" color={textColor}>
              {String(user.onboardingStep ?? '-')}
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.500">
              Created At
            </Text>
            <Text fontSize="md" color={textColor}>
              {formatDate(user.createdAt)}
            </Text>
          </Box>
        </SimpleGrid>
      </Card>

      {/* Business Associations */}
      <BusinessAssociationsPanel user={user} primaryBusiness={userBusiness} />

      {/* Quick Actions */}
      <Card p="30px">
        <Text color={textColor} fontSize="xl" fontWeight="700" mb="15px">
          Quick Actions
        </Text>
        <Flex gap="12px" wrap="wrap">
          <Button
            size="sm"
            variant="outline"
            colorScheme="blue"
            borderRadius="8px"
            isLoading={saving}
            onClick={setOnboardingComplete}
            isDisabled={user.onboardingStep === 'completed' || user.onboardingStep === true}
          >
            Complete Onboarding
          </Button>
          <Button
            size="sm"
            variant="outline"
            colorScheme={user.isDeleted ? 'green' : 'red'}
            borderRadius="8px"
            isLoading={saving}
            onClick={toggleDelete}
          >
            {user.isDeleted ? 'Restore User' : 'Soft Delete User'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            colorScheme="purple"
            borderRadius="8px"
            onClick={() =>
              navigate(`/admin/brilliant_users/update_user/${user.id}`, {
                state: user,
              })
            }
          >
            Edit User
          </Button>
        </Flex>
      </Card>
    </Flex>
  );
}
